export const C = {
  bg: "#EDE4D4",
  bgDark: "#000000",
  sidebar: "#FFFFFF",
  sidebarBorder: "#E2D6C0",
  card: "#FFFFFF",
  cardBorder: "#E2D6C0",
  border: "#E2D6C0",
  accent: "#7B1416",
  accentHover: "#5C0F11",
  accentLight: "#F5EFE4",
  text: "#1C1917",
  textMid: "#3C3530",
  textMuted: "#8B6F47",
  textLight: "#A68A5A",
  green: "#C4A882",
  greenLight: "#E3F9E5",
  greenDeep: "#8B6F47",
  red: "#B33A1A",
  redLight: "#FEE5E5",
  redDeep: "#C53B30",
  blue: "#7B1416",
  blueLight: "#F5EFE4",
  blueDeep: "#5C0F11",
  purple: "#8B6F47",
  purpleLight: "#F5E6FD",
  purpleDeep: "#8B2FB5",
  orange: "#C4A882",
  orangeLight: "#FFF4E5",
  orangeDeep: "#A68A5A",
  amber: "#C4A882",
  amberLight: "#FFF4E5",
  gold: "#FFCC00",
  goldLight: "#FFFADC",
  teal: "#C4A882",
  tealLight: "#F5EFE4",
  tealDeep: "#A68A5A",
  pink: "#FF2D55",
  pinkLight: "#FFE8ED",
  gray: "#8B6F47",
  forest: "#7B1416",
  forestDeep: "#5C0F11",
  mustard: "#C4A882",
  brand: "#7B1416",
  brandDeep: "#5C0F11",
} as const;

// Premium mesh-gradient backdrops for hero surfaces
export const MESH_BLUE = `radial-gradient(at 20% 20%, #A42022 0%, transparent 45%), radial-gradient(at 85% 10%, #7B1416 0%, transparent 50%), radial-gradient(at 70% 85%, #5C0F11 0%, transparent 55%)`;
export const MESH_GREEN = `radial-gradient(at 25% 15%, #C4A882 0%, transparent 45%), radial-gradient(at 80% 20%, #D4B89A 0%, transparent 50%), radial-gradient(at 60% 85%, #A68A5A 0%, transparent 55%)`;
export const MESH_PURPLE = `radial-gradient(at 20% 20%, #8B6F47 0%, transparent 45%), radial-gradient(at 85% 15%, #A68A5A 0%, transparent 50%), radial-gradient(at 60% 80%, #6B5535 0%, transparent 55%)`;
export const MESH_SUNSET = `radial-gradient(at 20% 20%, #C4A882 0%, transparent 45%), radial-gradient(at 80% 15%, #D4B89A 0%, transparent 50%), radial-gradient(at 60% 85%, #A68A5A 0%, transparent 55%)`;
export const MESH_TEAL = `radial-gradient(at 25% 20%, #C4A882 0%, transparent 45%), radial-gradient(at 80% 25%, #D4B89A 0%, transparent 50%), radial-gradient(at 60% 85%, #A68A5A 0%, transparent 55%)`;
export const MESH_FOREST = `radial-gradient(at 20% 15%, #7B1416 0%, transparent 50%), radial-gradient(at 85% 20%, #A42022 0%, transparent 50%), radial-gradient(at 60% 85%, #5C0F11 0%, transparent 55%)`;

// Premium layered shadow presets
export const SHADOWS = {
  float: "0 2px 8px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.03)",
  premium: "0 4px 24px -8px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.04)",
  lift: "0 8px 32px -12px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.05)",
  glow: "0 8px 40px -12px rgba(123,20,22,0.22)",
  glowGreen: "0 8px 40px -12px rgba(196,168,130,0.20)",
  glowPurple: "0 8px 40px -12px rgba(139,111,71,0.20)",
  glowOrange: "0 8px 40px -12px rgba(196,168,130,0.20)",
  inner: "inset 0 1px 2px rgba(0,0,0,0.04)",
} as const;

export const ACCENT_GRADIENT = `linear-gradient(135deg, ${C.brand} 0%, #A42022 100%)`;
export const FOREST_GRADIENT = `linear-gradient(135deg, ${C.forest} 0%, ${C.forestDeep} 100%)`;
export const BRAND_GRADIENT = `linear-gradient(135deg, ${C.brand} 0%, #A42022 100%)`;
export const GREEN_GRADIENT = `linear-gradient(135deg, #C4A882 0%, #A68A5A 100%)`;
export const SUNSET_GRADIENT = `linear-gradient(135deg, #C4A882 0%, #A68A5A 100%)`;
export const PURPLE_GRADIENT = `linear-gradient(135deg, #8B6F47 0%, #6B5535 100%)`;
export const TEAL_GRADIENT = `linear-gradient(135deg, #C4A882 0%, #A68A5A 100%)`;
export const CHILD_COLORS = ["#7B1416", "#C4A882", "#A42022", "#8B6F47", "#A68A5A", "#D4B89A"];

export type Student = {
  id: string; full_name: string; initials: string; color_hex: string; student_no: string;
  class_label: string; boarding: boolean; bus_route: string | null;
  status_label: string | null; status_dot: string; status_location: string | null;
  balance_ugx: number; daily_cap_ugx: number | null;
  school_name?: string | null; class_name?: string | null; grade?: string | null; active?: boolean;
};

export type FeeTerm = { id: string; student_id: string; term_label: string; total_ugx: number; paid_ugx: number; installment: number; of_installments: number; next_due: string | null; autopay_method: string | null };
export type Ledger = { id: string; student_id: string | null; kind: string; direction: "credit" | "debit"; title: string; subtitle: string | null; amount_ugx: number; ref: string | null; occurred_at: string };
export type Insight = { id: string; body: string; action_label: string | null; status: string };
export type Visit = { id: string; student_id: string; visit_date: string; reason: string; nurse_note: string | null; status: string };
export type ParentWallet = { id: string; balance_ugx: number };
export type Checkpoint = { id: string; student_id: string | null; kind: string; location: string | null; scanned_at: string };
export type NavSection = "home" | "family" | "child" | "finance" | "messages" | "calendar" | "marketplace" | "documents" | "consent" | "settings";
export type ChildTab = "overview" | "academics" | "attendance" | "finance" | "health" | "transport" | "messages" | "documents" | "timeline";

export const DEFAULT_CHILD: Student = {
  id: "demo-child-1",
  full_name: "Emma Nakamya",
  initials: "EN",
  color_hex: C.orange,
  student_no: "STU-2024-0042",
  class_label: "Primary 6",
  boarding: false,
  bus_route: "Route B - Kampala Road",
  status_label: "In Class",
  status_dot: "green",
  status_location: "Main Building, Room 6A",
  balance_ugx: 45000,
  daily_cap_ugx: 15000,
  school_name: "Greenhill Academy",
  class_name: "Primary 6A",
  active: true,
};

export function childColor(students: Student[], id: string) {
  const idx = students.findIndex(s => s.id === id);
  return CHILD_COLORS[Math.max(0, idx) % CHILD_COLORS.length];
}

export function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return { text: "Good morning", icon: "morning" as const };
  if (h < 17) return { text: "Good afternoon", icon: "afternoon" as const };
  return { text: "Good evening", icon: "evening" as const };
}
